import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { DEMO_ACCESS_MESSAGES, isDemoCodeComplete, normalizeDemoCode } from '@/lib/demoAccess'
import { requestDemoToken } from './demoAccessApi'

interface DemoCodeScreenProps {
    onUnlock: (token: string) => void
    initialError?: string
}

export const DemoCodeScreen = ({ onUnlock, initialError }: DemoCodeScreenProps) => {
    const [code, setCode] = useState('')
    const [error, setError] = useState(initialError ?? '')
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (loading || !isDemoCodeComplete(code)) return
        setLoading(true)
        setError('')
        const result = await requestDemoToken(code)
        setLoading(false)
        if (result.ok) {
            onUnlock(result.value.token)
            return
        }
        setError(DEMO_ACCESS_MESSAGES[result.failure])
        // Only a wrong code is wiped; after a network or server problem it is worth retrying as typed.
        if (result.failure === 'invalid') setCode('')
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="flex justify-center">
                        <img src="/logo_doucheflux.jpeg" alt="DoucheFLUX Housing First" className="h-40 object-contain" />
                    </div>
                    <h1 className="mt-6 text-center text-lg font-light text-gray-600">Démo Housing First</h1>
                    <p className="mt-2 text-center text-sm text-gray-600">Veuillez entrer le code.</p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="rounded-md bg-red-50 p-4">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}
                    <div>
                        <label htmlFor="demo-code" className="sr-only">Code</label>
                        <input
                            id="demo-code"
                            name="code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            required
                            value={code}
                            onChange={(e) => { if (!loading) setCode(normalizeDemoCode(e.target.value)) }}
                            placeholder="••••••"
                            className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md text-center text-2xl tracking-[0.5em] text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-primary focus:border-primary"
                        />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading || !isDemoCodeComplete(code)}>
                        {loading ? 'Vérification...' : 'Entrer'}
                    </Button>
                </form>
            </div>
        </div>
    )
}
